import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { AnimatePresence, MotionConfig } from 'framer-motion';

// Components
import Navigation from './components/Navigation';
import CustomCursor from './components/CustomCursor';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import Home from './pages/Home';
import ImmersiveExperiences from './pages/ImmersiveExperiences';
import DigitalInteractive from './pages/DigitalInteractive';
import Concepts from './pages/Concepts';
import OliviaRodrigo from './pages/OliviaRodrigo';
import OliviaRodrigoStandalone from './pages/OliviaRodrigoStandalone';
import About from './pages/About';
import Lab from './pages/Lab';
import Consulting from './pages/Consulting';

// Product portfolio (self-contained mini-site)
import ProductNav from './pages/product/ProductNav';
import ProductFooter from './pages/product/ProductFooter';
import ProductLanding from './pages/product/ProductLanding';
import ProductAbout from './pages/product/ProductAbout';
import CaseStudyRoute from './pages/product/CaseStudyRoute';
import OurStory from './pages/OurStory';
import PickleballFitness from './pages/PickleballFitness';
import KineticTypography from './pages/KineticTypography';

import './components.css';
import './pages.css';

// Heavy standalone routes — lazy-loaded so firebase + html2canvas stay OUT of the
// main bundle (they're only used by these two pages, never by /product).
const OurPlaybook = lazy(() => import('./pages/OurPlaybook'));
const LocalsOnly = lazy(() => import('./pages/LocalsOnly'));

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <Suspense fallback={null}>
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Hub */}
        <Route path="/" element={<Navigate to="/product" replace />} />

        {/* Product portfolio — self-contained mini-site */}
        <Route path="/product" element={<ProductLanding />} />
        <Route path="/product/about" element={<ProductAbout />} />
        <Route path="/product/:slug" element={<CaseStudyRoute />} />

        {/* Experiential portfolio */}
        <Route path="/experiential" element={<Home />} />
        <Route path="/experiential/immersive" element={<ImmersiveExperiences />} />
        <Route path="/experiential/digital" element={<DigitalInteractive />} />
        <Route path="/experiential/concepts" element={<Concepts />} />
        <Route path="/experiential/concepts/olivia-rodrigo" element={<OliviaRodrigo />} />
        <Route path="/experiential/about" element={<About />} />

        {/* Legacy routes — keep working for existing links */}
        <Route path="/immersive" element={<ImmersiveExperiences />} />
        <Route path="/digital" element={<DigitalInteractive />} />
        <Route path="/concepts" element={<Concepts />} />
        <Route path="/concepts/olivia-rodrigo" element={<OliviaRodrigo />} />
        <Route path="/about" element={<About />} />

        {/* Consulting */}
        <Route path="/consulting" element={<Consulting />} />

        {/* Lab */}
        <Route path="/lab" element={<Lab />} />

        {/* Standalone experiences */}
        <Route path="/olivia-rodrigo" element={<OliviaRodrigoStandalone />} />
        <Route path="/our-story" element={<OurStory />} />
        <Route path="/our-playbook" element={<OurPlaybook />} />
        <Route path="/pickleball-fitness" element={<PickleballFitness />} />
        <Route path="/locals-only" element={<LocalsOnly />} />
        <Route path="/kinetic-typography" element={<KineticTypography />} />
      </Routes>
    </AnimatePresence>
    </Suspense>
  );
}

function AppContent() {
  const location = useLocation();
  const path = location.pathname;

  // No nav/footer — hub + standalone experiences
  const isStandalonePage = path === '/'
    || path === '/our-story'
    || path === '/our-playbook'
    || path === '/olivia-rodrigo'
    || path === '/pickleball-fitness'
    || path === '/locals-only'
    || path === '/kinetic-typography';

  if (isStandalonePage) {
    return (
      <div className="App standalone-app">
        <AnimatedRoutes />
      </div>
    );
  }

  // Product portfolio is its own world — its own nav/footer, none of the studio chrome
  if (path.startsWith('/product')) {
    return (
      <div className="App product-app">
        <div className="product-root">
          <ProductNav />
          <main><AnimatedRoutes /></main>
          <ProductFooter />
        </div>
      </div>
    );
  }

  // Determine which section we're in for nav context
  let navSection = 'full';
  if (path.startsWith('/experiential') || path === '/immersive' || path === '/digital' || path.startsWith('/concepts')) {
    navSection = 'experiential';
  } else if (path.startsWith('/consulting')) {
    navSection = 'consulting';
  }

  return (
    <div className="App">
      <CustomCursor />
      <div className="noise-overlay" />
      <Navigation section={navSection} />
      <main>
        <AnimatedRoutes />
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <MotionConfig reducedMotion="user">
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      </MotionConfig>
      <Analytics />
    </Router>
  );
}

export default App;
